import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { Layout } from './components/Layout';
import { Login } from './components/Login';
import { Dashboard } from './pages/Dashboard';
import { BatchDetail } from './pages/BatchDetail';
import { AdminPanel } from './pages/AdminPanel';
import { Schedule } from './pages/Schedule';
import { Changelog } from './pages/Changelog';
import { ActionRequired } from './pages/ActionRequired';
import { AiAccuracy } from './pages/AiAccuracy';
import { Contact } from './pages/Contact';
import { RefundForecast } from './pages/RefundForecast';
import { ShipMemos } from './pages/ShipMemos';
import { ShipMemoDetail } from './pages/ShipMemoDetail';
import { FulfillmentSync } from './pages/FulfillmentSync';
import { FulfillmentSyncDetail } from './pages/FulfillmentSyncDetail';
import { StaleOrders } from './pages/StaleOrders';
import { BlockedRefunds } from './pages/BlockedRefunds';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

function AdminRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isAdmin, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (!isAdmin) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

function AppRoutes() {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900" />
      </div>
    );
  }

  return (
    <Routes>
      <Route
        path="/login"
        element={isAuthenticated ? <Navigate to="/" replace /> : <Login />}
      />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Layout>
              <Dashboard />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/ship-memos"
        element={
          <ProtectedRoute>
            <Layout>
              <ShipMemos />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/ship-memos/:date"
        element={
          <ProtectedRoute>
            <Layout>
              <ShipMemoDetail />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/fulfillment-sync"
        element={
          <ProtectedRoute>
            <Layout>
              <FulfillmentSync />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/fulfillment-sync/:date"
        element={
          <ProtectedRoute>
            <Layout>
              <FulfillmentSyncDetail />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/stale-orders"
        element={
          <ProtectedRoute>
            <Layout>
              <StaleOrders />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/blocked-refunds"
        element={
          <ProtectedRoute>
            <Layout>
              <BlockedRefunds />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/refund-forecast"
        element={
          <ProtectedRoute>
            <Layout>
              <RefundForecast />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/action-required"
        element={
          <ProtectedRoute>
            <Layout>
              <ActionRequired />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/ai-accuracy"
        element={
          <ProtectedRoute>
            <Layout>
              <AiAccuracy />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/batch/:date"
        element={
          <ProtectedRoute>
            <Layout>
              <BatchDetail />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin"
        element={
          <AdminRoute>
            <Layout>
              <AdminPanel />
            </Layout>
          </AdminRoute>
        }
      />
      <Route
        path="/admin/schedule"
        element={
          <AdminRoute>
            <Layout>
              <Schedule />
            </Layout>
          </AdminRoute>
        }
      />
      <Route
        path="/changelog"
        element={
          <ProtectedRoute>
            <Layout>
              <Changelog />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/contact"
        element={
          <ProtectedRoute>
            <Layout>
              <Contact />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
}

export default App;
